import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Dropdown,
  Layout,
  Space,
  Typography,
  Grid,
  message
} from 'antd';
import './Header.scss';
import { LoginOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../AppRouter/AppRouter';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/images/logo.svg';
import logoWhite from '../../assets/images/logoWhite.svg';
import CartIcon from '../CartIcon/CartIcon';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { getUser } from '../../redux/selectors';
import AuthModalsProvider from '../modals/AuthModalsProvider';
import { deleteFromCart } from '../../http/cart';
import { updateLanguage } from '../../http/user';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';

const Header = ({ user, cart, language, setLanguage, changeUserLanguage }) => {
  const { id: userId } = user;
  const breakpoints = Grid.useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();

  const { manualExit } = useSessionTimeout(dispatch, () => {}, () => {}, () => {});

  const [signInOpen, setSignInOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);

  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleMobileMenu = () => {
    if (mobileOpen) {
      setMobileOpen(false);
      document.body.style.overflowY = 'auto';
    } else {
      setMobileOpen(true);
      document.body.style.overflowY = 'hidden';
    }
  };

  const items = useMemo(() => {
    return userId
      ? [
          {
            key: 'profile',
            onClick: () => navigate(ROUTES.PROFILE),
            label: (
              <Link style={{ fontSize: 24, fontWeight: 700 }}>
                {t('header.profile')}
              </Link>
            ),
            icon: <UserOutlined style={{ fontSize: 24 }} />
          },
          {
            key: 'logout',
            onClick: manualExit,
            label: (
              <Typography
                style={{ fontSize: 24, fontWeight: 700, color: '#FAD732' }}
              >
                {t('header.logout')}
              </Typography>
            ),
            icon: <LogoutOutlined style={{ fontSize: 24, color: '#FAD732' }} />
          }
        ]
      : [
          {
            key: 'login',
            onClick: () => setSignInOpen(true),
            label: (
              <Typography style={{ fontSize: 24, fontWeight: 700 }}>
                {t('header.signIn')}
              </Typography>
            ),
            icon: <LoginOutlined style={{ fontSize: 24 }} />
          },
          {
            key: 'reg',
            onClick: () => setSignUpOpen(true),
            label: (
              <Typography style={{ fontSize: 24, fontWeight: 700 }}>
                {t('header.signUp')}
              </Typography>
            ),
            icon: <LoginOutlined style={{ fontSize: 24 }} />
          }
        ];
  }, [language, userId]);

  const onRemoveCartItem = async (product) => {
    try {
      await dispatch(deleteFromCart(product.id, product.denominationId));
    } catch (error) {
      messageApi.error({
        type: 'error',
        content: error.message ?? 'Failed to remove item from cart'
      });
    }
  };

  return (
    <React.Fragment>
      {contextHolder}
      <div className='header-container'>
        <Layout.Header className='header' id='header'>
          <img
            className='header__logo'
            src={!mobileOpen ? logo : logoWhite}
            onClick={() => navigate(ROUTES.MAIN)}
            alt=''
          />

          {/* <div className='header__menu'>
            <a href={t('links.products')}>{t('header.services')}</a>
            <a href={t('links.private')}>{t('header.private')}</a>
            <a href={t('links.about')}>{t('header.about')}</a>
            <a href={t('links.news')}>{t('header.news')}</a>
          </div> */}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'start',
              gap: breakpoints.xl
                ? 40
                : breakpoints.lg
                  ? 10
                  : breakpoints.md
                    ? 20
                    : breakpoints.sm
                      ? 15
                      : 10,
              justifySelf: 'end'
            }}
          >
            <LanguageSwitcher setLanguage={setLanguage} changeUserLanguage={changeUserLanguage} />
            <Space align='center' style={{ zIndex: 99999 }}>
              <CartIcon cart={cart} onRemove={onRemoveCartItem} />
              <div style={{ display: 'flex' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {![ROUTES.AUTH, ROUTES.REG].includes(location.pathname) && (
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                    >
                      <div className='avatar-container'>
                        <Dropdown
                          menu={{
                            items,
                            style: {
                              backgroundColor: 'white',
                              borderRadius: 30,
                              padding: 30,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 25
                            }
                          }}
                          trigger='click'
                        >
                          {!Boolean(userId) ? (
                            <Avatar
                              style={{
                                cursor: 'pointer',
                                backgroundColor: 'rgba(18, 18, 18, 1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: '0.3s',
                                '&:hover': {
                                  backgroundColor: 'black'
                                }
                              }}
                              size={
                                breakpoints.xl
                                  ? 50
                                  : breakpoints.lg
                                    ? 40
                                    : breakpoints.md
                                      ? 46
                                      : 40
                              }
                              icon={<UserOutlined style={{ fontSize: 20 }} />}
                            />
                          ) : (
                            <Avatar
                              style={{
                                cursor: 'pointer',
                                backgroundColor: 'rgba(18, 18, 18, 1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: '0.3s',
                                '&:hover': {
                                  backgroundColor: 'black'
                                }
                              }}
                              size={
                                breakpoints.xl
                                  ? 50
                                  : breakpoints.lg
                                    ? 40
                                    : breakpoints.md
                                      ? 46
                                      : 40
                              }
                            >{user.firstname && user.lastname ? `${user.firstname?.[0].toUpperCase()}${user.lastname?.[0].toUpperCase()}` : `${user.email?.[0].toUpperCase()}`}</Avatar>
                          )}
                        </Dropdown>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Space>
            {/* <div
              onClick={toggleMobileMenu}
              className={`burger ${mobileOpen ? 'open' : ''}`}
            >
              <div></div>
              <div></div>
              <div></div>
            </div>
            <div className={`burger-menu ${mobileOpen ? 'open' : ''}`}>
              <a href='https://tigsipay.com/en/services-and-solutions'>
                {t('header.services')}
              </a>
              <a href='https://tigsipay.com/en/private-person'>
                {t('header.private')}
              </a>
              <a href='https://tigsipay.com/en/about'>
                {t('header.about')}
              </a>
              <a href='https://tigsipay.com/en/blog'>{t('header.news')}</a>
            </div> */}
          </div>

          <AuthModalsProvider
            signInOpen={signInOpen}
            signUpOpen={signUpOpen}
            setSignInOpen={setSignInOpen}
            setSignUpOpen={setSignUpOpen}
          />
        </Layout.Header>
      </div>
    </React.Fragment>
  );
};

Header.defaultProps = {
  showTitle: true
};

const mapStateToProps = (state) => ({
  language: state.app.language,
  cart: state.games.cart,
  user: getUser(state)
});

const mapDispatchToProps = (dispatch) => ({
  setLanguage: (language) =>
    dispatch({ type: 'App/SET_LANG', payload: language }),
  changeUserLanguage: (language) => dispatch(updateLanguage(language))
});

export default connect(mapStateToProps, mapDispatchToProps)(Header);
